import { useState } from 'react';
import { useResultData } from '../../context';
import { tokens } from '../../tokens';

interface Props {
  width?: number;
  height?: number;
}

interface Run {
  length: number;
  win: boolean;
}

const groupRuns = (series: Array<{ win: boolean; profit: number }>): Run[] => {
  const runs: Run[] = [];
  for (const entry of series) {
    const last = runs[runs.length - 1];
    if (last && last.win === entry.win) last.length++;
    else runs.push({ length: 1, win: entry.win });
  }
  return runs;
};

const StreakBar = ({ width = 1272, height = 60 }: Props) => {
  const { streakSeries } = useResultData();
  const [hovered, setHovered] = useState<number | null>(null);
  const runs = groupRuns(streakSeries);
  const total = runs.reduce((sum, run) => sum + run.length, 0);
  if (!total) {
    return (
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: '100%', height, display: 'block' }}
      />
    );
  }
  const wins = runs.filter((run) => run.win);
  const losses = runs.filter((run) => !run.win);
  const longestWin = wins.length
    ? Math.max(...wins.map((run) => run.length))
    : 0;
  const longestLoss = losses.length
    ? Math.max(...losses.map((run) => run.length))
    : 0;
  const padTop = 18;
  const barHeight = height - padTop - 4;

  let x = 0;
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      style={{ width: '100%', height, display: 'block' }}
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id="nc-streak-win" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={tokens.green} stopOpacity="1" />
          <stop offset="100%" stopColor={tokens.green} stopOpacity="0.6" />
        </linearGradient>
        <linearGradient id="nc-streak-loss" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={tokens.red} stopOpacity="1" />
          <stop offset="100%" stopColor={tokens.red} stopOpacity="0.6" />
        </linearGradient>
      </defs>

      {runs.map((run, i) => {
        const runWidth = (run.length / total) * width;
        const barX = x;
        const isLongestWin =
          run.win && run.length === longestWin && longestWin > 0;
        const isLongestLoss =
          !run.win && run.length === longestLoss && longestLoss > 0;
        const isLongest = isLongestWin || isLongestLoss;
        const isHovered = hovered === i;
        // The streak count sits above the bar — always for the longest
        // win/loss runs, and on hover for any run.
        const showCount = isLongest || isHovered;
        const rect = (
          <g
            key={i}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() =>
              setHovered((current) => (current === i ? null : current))
            }
          >
            <rect
              x={barX + 0.5}
              y={padTop}
              width={Math.max(runWidth - 1, 1)}
              height={barHeight}
              fill={run.win ? 'url(#nc-streak-win)' : 'url(#nc-streak-loss)'}
              rx="1.5"
              stroke={isHovered ? 'rgba(255,255,255,0.85)' : 'none'}
              strokeWidth={isHovered ? 1 : 0}
            />
            {run.length > 1 &&
              runWidth > 6 &&
              Array.from({ length: run.length - 1 }, (_, tickIndex) => {
                const tickX = barX + ((tickIndex + 1) / run.length) * runWidth;
                return (
                  <line
                    key={tickIndex}
                    x1={tickX}
                    y1={padTop + 2}
                    x2={tickX}
                    y2={padTop + barHeight - 2}
                    stroke="rgba(0,0,0,0.25)"
                    strokeWidth="0.5"
                  />
                );
              })}
            {showCount && (
              <text
                x={barX + runWidth / 2}
                y={12}
                fontSize={isHovered ? '10.5' : '9'}
                fill={run.win ? tokens.green : tokens.red}
                textAnchor="middle"
                fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                fontWeight="700"
              >
                ×{run.length}
              </text>
            )}
          </g>
        );
        x += runWidth;
        return rect;
      })}
    </svg>
  );
};

export default StreakBar;
